import React, { useEffect, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { Button, Modal, Form, Row, Col, Badge, Alert } from '@themesberg/react-bootstrap';
import appConfigService from "../../services/appConfigService";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const AppConfigList = () => {
  const [configs, setConfigs] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedConfigs, setSelectedConfigs] = useState([]);

  const [formData, setFormData] = useState({
    key: "",
    value: "",
    type: "string",
    description: "",
    is_sensitive: false,
    is_active: true
  });

  const [bulkFormData, setBulkFormData] = useState({
    configs: []
  });

  const [filters, setFilters] = useState({
    search: "",
    type: "",
    is_active: "",
    is_sensitive: ""
  });

  const typeOptions = ["string", "json", "boolean", "integer"];

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};

      if (filters.search) params.search = filters.search;
      if (filters.type) params.type = filters.type;
      if (filters.is_active !== "") params.is_active = filters.is_active;
      if (filters.is_sensitive !== "") params.is_sensitive = filters.is_sensitive;

      const response = await appConfigService.getAll(params);

      if (response.success && response.data && response.data.data) {
        setConfigs(response.data.data);
      }
      setError("");
    } catch (error) {
      console.error("Error fetching configs:", error);
      setError(error.message || "Error al cargar configuraciones");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleQuickFilter = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      key: "",
      value: "",
      type: "string",
      description: "",
      is_sensitive: false,
      is_active: true
    });
    setEditingConfig(null);
    setError("");
    setSuccess("");
  };

  const handleCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      key: config.key,
      value: config.value || "",
      type: config.type,
      description: config.description || "",
      is_sensitive: config.is_sensitive,
      is_active: config.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estas seguro de eliminar esta configuracion?")) return;

    try {
      setLoading(true);
      const response = await appConfigService.delete(id);
      setSuccess(response.message);
      fetchConfigs();
    } catch (error) {
      console.error("Error deleting config:", error);
      setError(error.message || "Error al eliminar configuracion");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      setLoading(true);
      const response = await appConfigService.toggleActive(id);
      setSuccess(response.message);
      fetchConfigs();
    } catch (error) {
      console.error("Error toggling config:", error);
      setError(error.message || "Error al cambiar estado");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");

      // Validar datos antes del envio
      const validation = appConfigService.validateData(formData);
      if (!validation.isValid) {
        setError("Error de validacion: " + Object.values(validation.errors).join(", "));
        return;
      }

      let response;
      if (editingConfig) {
        response = await appConfigService.update(editingConfig.id, formData);
      } else {
        response = await appConfigService.create(formData);
      }

      setSuccess(response.message);
      setShowModal(false);
      resetForm();
      fetchConfigs();
    } catch (error) {
      console.error("Error saving config:", error);
      setError(error.message || "Error al guardar configuracion");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdate = () => {
    setBulkFormData({ configs: [] });
    setShowBulkModal(true);
  };

  const addBulkConfig = () => {
    setBulkFormData(prev => ({
      configs: [...prev.configs, { key: "", value: "" }]
    }));
  };

  const updateBulkConfig = (index, field, value) => {
    setBulkFormData(prev => ({
      configs: prev.configs.map((config, i) =>
        i === index ? { ...config, [field]: value } : config
      )
    }));
  };

  const removeBulkConfig = (index) => {
    setBulkFormData(prev => ({
      configs: prev.configs.filter((_, i) => i !== index)
    }));
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");

      const response = await appConfigService.bulkUpdate(bulkFormData.configs);
      setSuccess(response.message);
      setShowBulkModal(false);
      setBulkFormData({ configs: [] });
      fetchConfigs();
    } catch (error) {
      console.error("Error bulk updating configs:", error);
      setError(error.message || "Error en actualizacion masiva");
    } finally {
      setLoading(false);
    }
  };

  const columnDefs = [
    {
      headerName: "Clave",
      field: "key",
      width: 200,
      cellStyle: { fontFamily: 'monospace' }
    },
    {
      headerName: "Valor",
      field: "value",
      width: 250,
      cellRenderer: (params) => {
        if (params.data.is_sensitive) {
          return "********";
        }
        const value = params.value || "";
        return value.length > 50 ? `${value.substring(0, 50)}...` : value;
      }
    },
    {
      headerName: "Tipo",
      field: "type",
      width: 100,
      cellRenderer: (params) => (
        <Badge bg="info">{params.value}</Badge>
      )
    },
    {
      headerName: "Descripcion",
      field: "description",
      width: 250
    },
    {
      headerName: "Sensible",
      field: "is_sensitive",
      width: 100,
      cellRenderer: (params) => (
        <Badge bg={params.value ? "warning" : "secondary"}>
          {params.value ? "Si" : "No"}
        </Badge>
      )
    },
    {
      headerName: "Activo",
      field: "is_active",
      width: 100,
      cellRenderer: (params) => (
        <Badge bg={params.value ? "success" : "danger"}>
          {params.value ? "Si" : "No"}
        </Badge>
      )
    },
    {
      headerName: "Acciones",
      field: "actions",
      width: 250,
      cellRenderer: (params) => (
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleEdit(params.data)}
            className="me-2"
          >
            Editar
          </Button>
          <Button
            variant={params.data.is_active ? "outline-warning" : "outline-success"}
            size="sm"
            onClick={() => handleToggleActive(params.data.id)}
            className="me-2"
          >
            {params.data.is_active ? "Desactivar" : "Activar"}
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(params.data.id)}
          >
            Eliminar
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="ag-theme-alpine" style={{ width: "100%", padding: "1rem" }}>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Configuraciones de Aplicacion</h4>
        <div>
          <Button variant="outline-secondary" onClick={handleBulkUpdate} className="me-2">
            Actualizacion Masiva
          </Button>
          <Button onClick={handleCreate}>
            Nueva Configuracion
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Buscar por clave o descripcion..."
            value={filters.search}
            onChange={handleQuickFilter}
          />
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
          >
            <option value="">Todos los tipos</option>
            {typeOptions.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.is_active}
            onChange={(e) => handleFilterChange('is_active', e.target.value)}
          >
            <option value="">Todos los estados</option>
            <option value="1">Activos</option>
            <option value="0">Inactivos</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.is_sensitive}
            onChange={(e) => handleFilterChange('is_sensitive', e.target.value)}
          >
            <option value="">Todos</option>
            <option value="1">Sensibles</option>
            <option value="0">No sensibles</option>
          </Form.Select>
        </Col>
      </Row>

      <AgGridReact
        rowData={configs}
        columnDefs={columnDefs}
        onGridReady={(params) => setGridApi(params.api)}
        domLayout="autoHeight"
        animateRows={true}
        loading={loading}
      />

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingConfig ? "Editar Configuracion" : "Nueva Configuracion"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Clave *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({...formData, key: e.target.value})}
                    required
                    pattern="^[a-z][a-z0-9_]*$"
                    title="Solo minusculas, numeros y guiones bajos"
                    placeholder="config_key"
                  />
                  <Form.Text className="text-muted">
                    Solo minusculas, numeros y guiones bajos
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo *</Form.Label>
                  <Form.Select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    required
                  >
                    {typeOptions.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Valor</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                placeholder="Valor de la configuracion"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripcion</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                maxLength={1000}
                placeholder="Descripcion de la configuracion"
              />
              <Form.Text className="text-muted">
                Maximo 1000 caracteres
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Valor sensible (contraseña/token)"
                  checked={formData.is_sensitive}
                  onChange={(e) => setFormData({...formData, is_sensitive: e.target.checked})}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Configuracion activa"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Guardando..." : (editingConfig ? "Actualizar" : "Crear")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Bulk Update Modal */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Actualizacion Masiva</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleBulkSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <div className="mb-3">
              <Button variant="outline-primary" onClick={addBulkConfig}>
                Agregar Configuracion
              </Button>
            </div>

            {bulkFormData.configs.map((config, index) => (
              <Row key={index} className="mb-3 align-items-end">
                <Col md={4}>
                  <Form.Label>Clave</Form.Label>
                  <Form.Control
                    type="text"
                    value={config.key}
                    onChange={(e) => updateBulkConfig(index, 'key', e.target.value)}
                    required
                    placeholder="config_key"
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Valor</Form.Label>
                  <Form.Control
                    type="text"
                    value={config.value}
                    onChange={(e) => updateBulkConfig(index, 'value', e.target.value)}
                    placeholder="Valor"
                  />
                </Col>
                <Col md={2}>
                  <Button
                    variant="outline-danger"
                    onClick={() => removeBulkConfig(index)}
                  >
                    Eliminar
                  </Button>
                </Col>
              </Row>
            ))}

            {bulkFormData.configs.length === 0 && (
              <Alert variant="info">
                No hay configuraciones para actualizar. Haz clic en "Agregar Configuracion" para comenzar.
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || bulkFormData.configs.length === 0}
            >
              {loading ? "Actualizando..." : "Actualizar Configuraciones"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AppConfigList;